import { Fade, FadeProps } from '@mui/material';
import { useCallback, useMemo, useState } from 'react';

import { Null } from '@/components/Null';
import { NotificationContext, useNotifications } from '@/hooks/useNotifications';
import { useStableValue } from '@/hooks/useStableValue';

export const useDismissable = (notificationId?: string, context?: NotificationContext) => {
    const { dismissNotification } = useNotifications();
    const [dismissedThisSession, setDismissedThisSession] = useState(false);
    const stableContext = useStableValue(context);

    const dismissableProps = useMemo(
        () => ({ dismissedThisSession, notificationId, context: stableContext }),
        [dismissedThisSession, notificationId, stableContext]
    );

    const onDismiss = useCallback(() => {
        setDismissedThisSession(true);
        dismissNotification(notificationId, stableContext);
    }, [notificationId, dismissNotification, stableContext]);

    return { onDismiss, dismissableProps };
};

type DismissableProps = {
    notificationId?: string;
    context?: NotificationContext;
    dismissedThisSession: boolean;
} & Pick<FadeProps, 'children'>;

export const Dismissable = ({ dismissedThisSession, notificationId, context, children }: DismissableProps) => {
    const { isNotificationDismissed, isPending } = useNotifications();

    if (!notificationId) {
        return children;
    }

    if (isPending) {
        return <></>;
    }

    if (isNotificationDismissed(notificationId, context) && !dismissedThisSession) {
        return <Null data-testid={`dismissed-${notificationId}`} />;
    }
    return (
        <Fade in={!dismissedThisSession} timeout={{ enter: 0, exit: 500 }} unmountOnExit>
            {children}
        </Fade>
    );
};
